import {
  filter,
  tap,
  ignoreElements,
  switchMap,
  mergeMap,
  catchError,
  concatMap,
} from 'rxjs/operators'
import {of, concat, from} from 'rxjs'
import {Epic} from 'redux-observable'
import {RootState, AppActions} from 'app/rootReducer'
import {PoliciesService} from 'services/api'
import {policiesActions} from './policiesSlice'
import {usersEpics} from './Users/usersEpics'
import {policiesTesterEpics} from './PoliciesTester/policiesTesterEpics'
import Toast from 'components/Basic/Toast'
import {toast} from 'react-toastify'
import {cloudAppEpics} from './CloudApp/cloudAppEpics'
import {policyErrorToast} from 'definitions/constant'
import {i18n} from 'i18n'
import {history} from 'utils'

const {t} = i18n

const updatePolicyNameEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(policiesActions.updatePolicyName.match),
    tap(({payload}) => localStorage.setItem('selectedPolicyName', payload)),
    ignoreElements(),
  )

const createPolicyEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(policiesActions.createPolicy.match),
    switchMap(({payload}) =>
      from(PoliciesService.createPolicy(payload.policyName)).pipe(
        concatMap(({data}) => {
          payload.callBackFunc()
          return of(data).pipe(
            mergeMap(() => [
              policiesActions.createPolicySuccess({
                ...data,
                policyName: payload.policyName,
              }),
            ]),
          )
        }),
        catchError((message: string) =>
          concat(of(policiesActions.createPolicyFailure(message))),
        ),
      ),
    ),
  )

const createPolicyFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.createPolicyFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

const createPolicySuccessEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.createPolicySuccess.match),
    mergeMap(data => {
      const {message, policyName} = data.payload
      if (message) {
        Toast({message: 'Policy Created Successfully', type: 'success'})
      }
      history.push({
        pathname: '/policies/content',
        state: {policyName: policyName},
      })
      return [policiesActions.updatePolicyName(policyName)]
    }),
  )

const deletePolicyEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(policiesActions.deletePolicy.match),
    switchMap(({payload}) =>
      from(PoliciesService.deletePolicy({policyName: payload.policyName})).pipe(
        concatMap(({data}) => {
          payload.callbackFunc()
          return of(data).pipe(
            mergeMap(() => [
              policiesActions.resetPolicyList(),
              policiesActions.deletePolicySuccess(
                'Policy deleted Successfully',
              ),
            ]),
          )
        }),
        catchError((message: string) =>
          concat(of(policiesActions.deletePolicyFailure(message))),
        ),
      ),
    ),
  )

const deletePolicyFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.deletePolicyFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

const deletePolicySuccessEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.deletePolicySuccess.match),
    tap(({payload: message}) => Toast({message, type: 'success'})),
    ignoreElements(),
  )

const updatePolicyUpdateFieldEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.updatePolicyUpdateField.match),
    tap(({payload}) => {
      if (
        payload?.throwError &&
        (payload.throwError1 || payload.throwError2) &&
        (payload?.value1 || payload?.value2)
      ) {
        if (!toast.isActive(policyErrorToast)) {
          Toast({
            message: t('common:TAB_ENTER_ERROR'),
            type: 'error',
            customId: policyErrorToast,
          })
        }
      }
    }),
    ignoreElements(),
  )

export const policiesEpics = [
  usersEpics,
  policiesTesterEpics,
  cloudAppEpics,
  [
    createPolicyEpic,
    createPolicyFailureEpic,
    createPolicySuccessEpic,
    deletePolicyEpic,
    deletePolicyFailureEpic,
    deletePolicySuccessEpic,
    updatePolicyNameEpic,
    updatePolicyUpdateFieldEpic,
  ],
].flatMap(epic => epic)
